import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Accordion, Row, Col, Alert, ListGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { tenantsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Tenant {
  id: string
  name: string
  domain: string
  is_active: boolean
  max_extensions: number
  language?: string
  carddav_enabled?: boolean
  blf_hide_caller_info?: boolean
  ldap_max_results?: number
  provisioning_admin_password?: string
  smtp_host?: string
  smtp_port?: number
  smtp_user?: string
  smtp_password?: string
  smtp_from_email?: string
  smtp_from_name?: string
  smtp_tls?: boolean
  smtp_ssl?: boolean
}

interface DomainChangeImpact {
  old_domain: string
  new_domain: string
  extension_count: number
  affected_items: { type: string; description: string; old_value: string; new_value: string }[]
  warnings: string[]
}

const emptyForm = {
  name: '',
  domain: '',
  max_extensions: 100,
  is_active: true,
  language: 'de',
  carddav_enabled: true,
  blf_hide_caller_info: false,
  ldap_max_results: 500,
  provisioning_admin_password: 'YeaLinK507',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: '',
  smtp_tls: true,
  smtp_ssl: false
}

export default function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const { selectedTenant, setSelectedTenant } = useAuthStore()
  const confirm = useConfirm()

  // Domain change warning
  const [showDomainWarning, setShowDomainWarning] = useState(false)
  const [domainChangeImpact, setDomainChangeImpact] = useState<DomainChangeImpact | null>(null)
  const [pendingSubmitData, setPendingSubmitData] = useState<typeof emptyForm | null>(null)

  useEffect(() => {
    loadTenants()
  }, [])

  const loadTenants = async () => {
    try {
      const response = await tenantsApi.list()
      setTenants(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingTenant(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (tenant: Tenant) => {
    setEditingTenant(tenant)
    setFormData({
      name: tenant.name,
      domain: tenant.domain,
      max_extensions: tenant.max_extensions,
      is_active: tenant.is_active,
      language: tenant.language || 'de',
      carddav_enabled: tenant.carddav_enabled ?? true,
      blf_hide_caller_info: tenant.blf_hide_caller_info ?? false,
      ldap_max_results: tenant.ldap_max_results ?? 500,
      provisioning_admin_password: tenant.provisioning_admin_password || 'YeaLinK507',
      smtp_host: tenant.smtp_host || '',
      smtp_port: tenant.smtp_port || 587,
      smtp_user: tenant.smtp_user || '',
      smtp_password: '',
      smtp_from_email: tenant.smtp_from_email || '',
      smtp_from_name: tenant.smtp_from_name || '',
      smtp_tls: tenant.smtp_tls ?? true,
      smtp_ssl: tenant.smtp_ssl ?? false
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prepare data - don't send empty password on update
    const submitData = { ...formData }
    if (editingTenant && !submitData.smtp_password) {
      delete (submitData as Record<string, unknown>).smtp_password
    }

    // Check if domain is being changed on an existing tenant
    if (editingTenant && formData.domain !== editingTenant.domain) {
      try {
        const response = await tenantsApi.domainChangeImpact(editingTenant.id, formData.domain)
        setDomainChangeImpact(response.data)
        setPendingSubmitData(submitData)
        setShowDomainWarning(true)
        return // Don't proceed, wait for user confirmation
      } catch {
        toast.error('Fehler beim Prüfen der Domain-Änderung')
        return
      }
    }

    // No domain change or new tenant - proceed directly
    await performSubmit(submitData)
  }

  const performSubmit = async (submitData: typeof emptyForm) => {
    try {
      if (editingTenant) {
        await tenantsApi.update(editingTenant.id, submitData)
        toast.success('Mandant aktualisiert')
      } else {
        await tenantsApi.create(submitData)
        toast.success('Mandant erstellt')
      }
      setShowModal(false)
      setEditingTenant(null)
      setFormData(emptyForm)
      loadTenants()
    } catch {
      toast.error(editingTenant ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen')
    }
  }

  const handleConfirmDomainChange = async () => {
    if (pendingSubmitData) {
      await performSubmit(pendingSubmitData)
    }
    setShowDomainWarning(false)
    setDomainChangeImpact(null)
    setPendingSubmitData(null)
  }

  const handleCancelDomainChange = () => {
    setShowDomainWarning(false)
    setDomainChangeImpact(null)
    setPendingSubmitData(null)
  }

  const handleDelete = async (tenant: Tenant) => {
    const confirmed = await confirm({
      title: 'Mandant löschen',
      message: `Mandant "${tenant.name}" wirklich löschen? Alle zugehörigen Daten (Nebenstellen, Benutzer, etc.) werden ebenfalls gelöscht!`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await tenantsApi.delete(tenant.id)
      toast.success('Mandant gelöscht')
      // Clear selection if deleted tenant was selected
      if (selectedTenant?.id === tenant.id) {
        setSelectedTenant(null)
      }
      loadTenants()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleSelectTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    toast.success(`Mandant "${tenant.name}" ausgewählt`)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingTenant(null)
    setFormData(emptyForm)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Mandanten</h4>
          {selectedTenant && (
            <small className="text-muted">
              <i className="bi bi-check-circle-fill text-success me-1"></i>
              Aktiver Mandant: <strong>{selectedTenant.name}</strong>
            </small>
          )}
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neuer Mandant
        </Button>
      </div>

      <div className="alert alert-info mb-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Hinweis:</strong> Wählen Sie einen Mandanten aus, um dessen Daten (Nebenstellen, Routen, etc.) zu bearbeiten.
        Der aktive Mandant wird im Header angezeigt.
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th style={{width: '40px'}}></th>
              <th>Name</th>
              <th>Domain</th>
              <th>Max. Nebenstellen</th>
              <th>Konto-Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Mandanten</td></tr>
            ) : (
              tenants.map(t => (
                <tr
                  key={t.id}
                  className={selectedTenant?.id === t.id ? 'table-primary' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelectTenant(t)}
                >
                  <td className="text-center">
                    {selectedTenant?.id === t.id ? (
                      <i className="bi bi-check-circle-fill text-success fs-5"></i>
                    ) : (
                      <i className="bi bi-circle text-muted"></i>
                    )}
                  </td>
                  <td>
                    <strong>{t.name}</strong>
                    {selectedTenant?.id === t.id && (
                      <Badge bg="success" className="ms-2">Ausgewählt</Badge>
                    )}
                  </td>
                  <td>{t.domain}</td>
                  <td>{t.max_extensions}</td>
                  <td>
                    <Badge bg={t.is_active ? 'success' : 'secondary'}>
                      {t.is_active ? 'Aktiviert' : 'Deaktiviert'}
                    </Badge>
                  </td>
                  <td className="text-end" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant={selectedTenant?.id === t.id ? 'success' : 'outline-success'}
                      size="sm"
                      className="me-1"
                      onClick={() => handleSelectTenant(t)}
                      title="Als aktiven Mandanten auswählen"
                    >
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => openEditModal(t)}
                      title="Bearbeiten"
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(t)}
                      title="Löschen"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTenant ? 'Mandant bearbeiten' : 'Neuer Mandant'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="z.B. Firma ABC"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Domain</Form.Label>
              <Form.Control
                value={formData.domain}
                onChange={(e) => setFormData({...formData, domain: e.target.value})}
                required
                placeholder="z.B. firma-abc.local"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max. Nebenstellen</Form.Label>
              <Form.Control
                type="number"
                value={formData.max_extensions}
                onChange={(e) => setFormData({...formData, max_extensions: parseInt(e.target.value) || 100})}
                min={1}
              />
            </Form.Group>
            {editingTenant && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="is_active"
                  label="Konto aktiviert (Mandant kann sich einloggen)"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <Form.Text className="text-muted">
                  Deaktivierte Mandanten können sich nicht einloggen, aber ihre Daten bleiben erhalten.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="carddav_enabled"
                label="CardDAV aktiviert (Telefonbuch-Zugriff via CardDAV)"
                checked={formData.carddav_enabled}
                onChange={(e) => setFormData({...formData, carddav_enabled: e.target.checked})}
              />
              <Form.Text className="text-muted">
                Ermöglicht Zugriff auf das Telefonbuch via CardDAV-Protokoll (Authentifizierung mit SIP-Zugangsdaten).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="blf_hide_caller_info"
                label="BLF-Tasten: nur Status anzeigen (keine Rufnummern)"
                checked={formData.blf_hide_caller_info}
                onChange={(e) => setFormData({...formData, blf_hide_caller_info: e.target.checked})}
              />
              <Form.Text className="text-muted">
                Besetztlampenfeld, Funktionstasten und Statusübersichten in den Apps und auf den Yealink-Telefonen
                zeigen nur noch frei / klingelt / besetzt — keine Nummer und keinen Namen des Gesprächspartners.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-translate me-2"></i>
                Sprache
              </Form.Label>
              <Form.Select
                value={formData.language}
                onChange={(e) => setFormData({...formData, language: e.target.value})}
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="it">Italiano</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Sprache für Ansagen (Warteschlangen, IVR, Voicemail etc.)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-book me-2"></i>
                LDAP Max. Ergebnisse
              </Form.Label>
              <Form.Control
                type="number"
                value={formData.ldap_max_results}
                onChange={(e) => setFormData({...formData, ldap_max_results: parseInt(e.target.value) || 500})}
                min={10}
                max={10000}
              />
              <Form.Text className="text-muted">
                Maximale Anzahl Einträge die vom LDAP-Server zurückgegeben werden (10-10'000).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-phone me-2"></i>
                Standard Admin-Passwort für Telefone
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.provisioning_admin_password}
                onChange={(e) => setFormData({...formData, provisioning_admin_password: e.target.value})}
                placeholder="YeaLinK507"
                maxLength={50}
              />
              <Form.Text className="text-muted">
                Standard-Passwort für das Admin-Web-Interface der provisionierten Telefone.
                Kann pro Vorlage überschrieben werden (wenn Vorlage "admin" als Passwort hat, wird dieses verwendet).
              </Form.Text>
            </Form.Group>

            <Accordion className="mt-4">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <i className="bi bi-envelope me-2"></i>
                  E-Mail / SMTP-Einstellungen
                  {formData.smtp_host && <Badge bg="success" className="ms-2">Konfiguriert</Badge>}
                </Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted small mb-3">
                    SMTP-Einstellungen für Voicemail-Benachrichtigungen und andere E-Mails dieses Mandanten.
                  </p>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>SMTP-Server</Form.Label>
                        <Form.Control
                          value={formData.smtp_host}
                          onChange={(e) => setFormData({...formData, smtp_host: e.target.value})}
                          placeholder="z.B. mail.example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Port</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.smtp_port}
                          onChange={(e) => setFormData({...formData, smtp_port: parseInt(e.target.value) || 587})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Benutzername</Form.Label>
                        <Form.Control
                          value={formData.smtp_user}
                          onChange={(e) => setFormData({...formData, smtp_user: e.target.value})}
                          placeholder="z.B. user@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Passwort</Form.Label>
                        <Form.Control
                          type="password"
                          value={formData.smtp_password}
                          onChange={(e) => setFormData({...formData, smtp_password: e.target.value})}
                          placeholder={editingTenant ? '(unverändert lassen wenn leer)' : ''}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Absender E-Mail</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.smtp_from_email}
                          onChange={(e) => setFormData({...formData, smtp_from_email: e.target.value})}
                          placeholder="z.B. voicemail@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Absender Name</Form.Label>
                        <Form.Control
                          value={formData.smtp_from_name}
                          onChange={(e) => setFormData({...formData, smtp_from_name: e.target.value})}
                          placeholder="z.B. Voicemail System"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="smtp_tls"
                        label="STARTTLS verwenden (Port 587)"
                        checked={formData.smtp_tls}
                        onChange={(e) => setFormData({...formData, smtp_tls: e.target.checked, smtp_ssl: e.target.checked ? false : formData.smtp_ssl})}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="smtp_ssl"
                        label="SSL/TLS verwenden (Port 465)"
                        checked={formData.smtp_ssl}
                        onChange={(e) => setFormData({...formData, smtp_ssl: e.target.checked, smtp_tls: e.target.checked ? false : formData.smtp_tls})}
                      />
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingTenant ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Domain Change Warning Modal */}
      <Modal show={showDomainWarning} onHide={handleCancelDomainChange} size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Domain-Änderung bestätigen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {domainChangeImpact && (
            <>
              <Alert variant="warning">
                <strong>Achtung:</strong> Sie ändern die Domain von <code>{domainChangeImpact.old_domain}</code> zu <code>{domainChangeImpact.new_domain}</code>.
                Dies betrifft <strong>{domainChangeImpact.extension_count}</strong> Nebenstellen und erfordert Neukonfiguration aller Telefone.
              </Alert>

              <h6 className="mb-3">
                <i className="bi bi-list-check me-2"></i>
                Betroffene Konfigurationen:
              </h6>
              <ListGroup className="mb-4" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {domainChangeImpact.affected_items.map((item, idx) => (
                  <ListGroup.Item key={idx} className="py-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <Badge bg={
                          item.type === 'sip_username' ? 'primary' :
                          item.type === 'carddav_url' ? 'info' :
                          item.type === 'ldap_base_dn' ? 'success' :
                          'secondary'
                        } className="me-2">
                          {item.type === 'sip_username' ? 'SIP' :
                           item.type === 'carddav_url' ? 'CardDAV' :
                           item.type === 'ldap_base_dn' ? 'LDAP' :
                           item.type === 'ldap_bind_dn' ? 'LDAP' : item.type}
                        </Badge>
                        <span>{item.description}</span>
                      </div>
                    </div>
                    <div className="mt-1 small">
                      <div className="text-danger"><i className="bi bi-dash-circle me-1"></i>{item.old_value}</div>
                      <div className="text-success"><i className="bi bi-plus-circle me-1"></i>{item.new_value}</div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <Alert variant="info" className="mb-0">
                <strong><i className="bi bi-info-circle me-2"></i>Hinweis:</strong>
                <ul className="mb-0 mt-2">
                  {domainChangeImpact.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDomainChange}>
            <i className="bi bi-x-lg me-1"></i>
            Abbrechen
          </Button>
          <Button variant="warning" onClick={handleConfirmDomainChange}>
            <i className="bi bi-check-lg me-1"></i>
            Domain ändern
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
